import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { DriftTypeBadge } from "./DriftTypeBadge";
import { driftTypeDescriptionKey, formatDriftTimestamp } from "./drift-alerts";
import type { DriftAlert } from "./types";

interface DriftAlertRowProps {
  readonly alert: DriftAlert;
  readonly onResolve: (alert: DriftAlert) => void;
}

interface FieldProps {
  readonly label: string;
  readonly value: string | null;
}

function Field({ label, value }: FieldProps) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="truncate font-mono text-xs text-foreground">
        {value ?? t("subscriptions.driftAlerts.none")}
      </p>
    </div>
  );
}

export function DriftAlertRow({ alert, onResolve }: DriftAlertRowProps) {
  const { t } = useTranslation();

  return (
    <article
      data-testid={`drift-alert-row-${alert.id}`}
      className="space-y-3 rounded-lg border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <DriftTypeBadge driftType={alert.driftType} testId={`drift-alert-type-${alert.id}`} />
            <span className="font-sans text-xs text-muted-foreground">
              {t("subscriptions.driftAlerts.detectedAt", {
                when: formatDriftTimestamp(alert.detectedAt),
              })}
            </span>
          </div>
          <p className="font-sans text-sm text-muted-foreground">
            {t(driftTypeDescriptionKey(alert.driftType))}
          </p>
        </div>
        <Button
          data-testid={`resolve-drift-alert-${alert.id}`}
          variant="outline"
          size="sm"
          onClick={() => onResolve(alert)}
        >
          {t("subscriptions.driftAlerts.actions.resolve")}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label={t("subscriptions.driftAlerts.fields.subscription")}
          value={alert.subscriptionId}
        />
        <Field
          label={t("subscriptions.driftAlerts.fields.localPrice")}
          value={alert.localPriceId}
        />
        <Field
          label={t("subscriptions.driftAlerts.fields.stripePrice")}
          value={alert.stripePriceId}
        />
        <Field
          label={t("subscriptions.driftAlerts.fields.pendingPrice")}
          value={alert.pendingPriceId}
        />
      </div>

      {alert.notes && (
        <div className="space-y-0.5">
          <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("subscriptions.driftAlerts.fields.notes")}
          </p>
          <p className="font-sans text-sm text-foreground">{alert.notes}</p>
        </div>
      )}
    </article>
  );
}
